import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Container } from '@mui/material'
import { DateRangePicker } from 'react-date-range'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import Popover from '@mui/material/Popover'
import InputAdornment from '@mui/material/InputAdornment'
import ArrowDropUp from '@mui/icons-material/KeyboardArrowUp'
import { KeyboardArrowDown } from '@mui/icons-material'
import IconButton from '@mui/material/IconButton'
import clsx from 'clsx'

import Paper from '@mui/material/Paper'
import {
  isSameDay,
  addDays,
  endOfDay,
  startOfDay,
  startOfMonth,
  endOfMonth,
  addMonths,
  startOfYear,
  endOfYear,
  format,
  isAfter,
  isBefore,
  addYears,
} from 'date-fns'
import moment from 'moment'
import { withStyles } from '~/themes/useStyles'
import { COLORS } from '~/themes/common'
import Icon from '~/components/common/Icon'
import TextInput from '~/components/common/TextInput'
import Button from '~/components/common/Button'
import style from './style'
import { CustomStaticRangeLabelContent } from './elements'
import { useWindowWidth } from '~/utils/hooks/useSize'
import {
  DAY_PICKER_DESKTOP_PROPS,
  DAY_PICKER_TABLET_PROPS,
  isTableMode,
} from '~/utils/layout'

const staticRangeHandler = {
  range: {},
  isSelected(range) {
    const definedRange = this.range()
    return (
      isSameDay(range.startDate, definedRange.startDate) &&
      isSameDay(range.endDate, definedRange.endDate)
    )
  },
  hasCustomRendering: true,
}

const TimeRange = (props) => {
  const {
    classes,
    labelText,
    placeholder,
    value,
    className,
    tableClassName,
    onApply,
    onClear,
    leftItem,
    disableFuture,
    maxDate,
    legend,
    removeAll,
    fixedHeight,
    timeZone,
    disableToday,
    customRanges,
    onClickRangeLabel,
    isAddOptionLast12Months,
    dataTestId,
    paperDataTestId,
  } = props

  // INIT DATE RANGE VALUE

  const day = timeZone
    ? moment().tz(timeZone).format('MM/DD/YYYY')
    : moment().format('MM/DD/YYYY')
  const yesterday = addDays(new Date(day), -1)
  const today = new Date(day)
  const currentUsedDay = disableToday ? yesterday : today
  const defineds = {
    theFirstestDay: startOfDay(new Date(0)),
    startOfToday: startOfDay(currentUsedDay),
    endOfToday: endOfDay(currentUsedDay),
    startOfYesterday: startOfDay(addDays(today, -1)),
    endOfYesterday: endOfDay(addDays(today, -1)),
    startOf3Days: startOfDay(addDays(currentUsedDay, -2)),
    endOf3Days: startOfDay(currentUsedDay),
    startOf7Days: startOfDay(addDays(today, -7)),
    endOf7Days: startOfDay(addDays(today, -1)),
    startOf30Days: startOfDay(addDays(today, -30)),
    endOf30Days: startOfDay(addDays(today, -1)),
    startOfMonth: startOfMonth(currentUsedDay),
    endOfMonth: endOfMonth(currentUsedDay),
    startOfLastMonth: startOfMonth(addMonths(today, -1)),
    endOfLastMonth: endOfMonth(addMonths(today, -1)),
    startOfYear: startOfYear(currentUsedDay),
    startOfLastYear: startOfYear(addYears(today, -1)),
    endOfLastYear: endOfYear(addYears(today, -1)),
    startOfLast12Months: startOfMonth(addMonths(today, -11)),
  }

  const createStaticRanges = (ranges) =>
    ranges.map((range) => ({ ...staticRangeHandler, ...range }))

  const defaultRanges = [
    {
      label: 'All Time',
      labelKey: 'ALL',
      range: () => ({
        startDate: null,
        endDate: null,
      }),
    },
    ...(disableToday
      ? []
      : [
          {
            label: 'Today',
            labelKey: 'today',
            range: () => ({
              startDate: defineds.startOfToday,
              endDate: defineds.endOfToday,
            }),
          },
        ]),
    {
      label: 'Yesterday',
      labelKey: 'yesterday',
      range: () => ({
        startDate: defineds.startOfYesterday,
        endDate: defineds.endOfYesterday,
      }),
    },

    {
      label: 'Last 7 days',
      labelKey: 'last7days',
      range: () => ({
        startDate: defineds.startOf7Days,
        endDate: defineds.endOf7Days,
      }),
    },
    {
      label: 'Last 30 days',
      labelKey: 'last30days',
      range: () => ({
        startDate: defineds.startOf30Days,
        endDate: defineds.endOf30Days,
      }),
    },
    {
      label: 'This Month',
      labelKey: 'thisMonth',
      range: () => ({
        startDate: defineds.startOfMonth,
        endDate: defineds.endOfMonth,
      }),
    },
    {
      label: 'Last Month',
      labelKey: 'lastMonth',
      range: () => ({
        startDate: defineds.startOfLastMonth,
        endDate: defineds.endOfLastMonth,
      }),
    },
    ...(isAddOptionLast12Months
      ? [
          {
            label: 'Last 12 months',
            labelKey: 'last12Months',
            range: () => ({
              startDate: defineds.startOfLast12Months,
              endDate: defineds.endOfToday,
            }),
          },
        ]
      : []),
    {
      label: 'Custom',
      labelKey: 'custom',
      range: () => ({
        startDate: defineds.startOf3Days,
        endDate: defineds.endOfToday,
      }),
    },
  ]
  const staticRanges = createStaticRanges(defaultRanges)

  const staticRangesOptional = createStaticRanges([
    ...(disableToday
      ? []
      : [
          {
            label: 'Today',
            labelKey: 'today',
            range: () => ({
              startDate: defineds.startOfToday,
              endDate: defineds.endOfToday,
            }),
          },
        ]),
    {
      label: 'Yesterday',
      labelKey: 'yesterday',
      range: () => ({
        startDate: defineds.startOfYesterday,
        endDate: defineds.endOfYesterday,
      }),
    },
    {
      label: 'Last 7 days',
      labelKey: 'last7days',
      range: () => ({
        startDate: defineds.startOf7Days,
        endDate: defineds.endOf7Days,
      }),
    },
    {
      label: 'Last 30 days',
      labelKey: 'last30days',
      range: () => ({
        startDate: defineds.startOf30Days,
        endDate: defineds.endOf30Days,
      }),
    },
    {
      label: 'Month to date',
      labelKey: 'monthToDate',
      range: () => ({
        startDate: defineds.startOfMonth,
        endDate: defineds.endOfToday,
      }),
    },
    {
      label: 'Year to date',
      labelKey: 'yearToDate',
      range: () => ({
        startDate: defineds.startOfYear,
        endDate: defineds.endOfToday,
      }),
    },
    {
      label: 'Last Year',
      labelKey: 'lastYear',
      range: () => ({
        startDate: defineds.startOfLastYear,
        endDate: defineds.endOfLastYear,
      }),
    },
    {
      label: 'Custom',
      labelKey: 'custom',
      range: () => ({
        startDate: defineds.startOf3Days,
        endDate: defineds.endOfToday,
      }),
    },
  ])

  // END OF INIT DATE RANGE VALUE

  const defaultRange = {
    startDate: null,
    endDate: null,
    key: 'selection',
  }

  const [dataRange, setDataRange] = useState([defaultRange])
  const [tempRange, setTempRange] = useState({
    startDate: '',
    endDate: '',
  })
  const [disablePreview, setDisabledPreview] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)

  const handleOpen = (event) => {
    if (!anchorEl) {
      setAnchorEl(event.currentTarget)
    }
  }

  const handleClose = () => {
    setDataRange([
      {
        ...defaultRange,
        startDate: value.startDate ? new Date(value.startDate) : null,
        endDate: value.endDate ? new Date(value.endDate) : null,
      },
    ])
    setAnchorEl(null)
  }

  const handleChange = (item) => {
    setDataRange([item.selection])
  }

  const handleInputChange = (e) => {
    setTempRange({
      ...tempRange,
      [e.target.name]: e.target.value,
    })
  }

  const handleInputBlur = (e) => {
    const { target } = e
    const isValidDate =
      moment(target.value, 'MM/DD/YYYY', true).isValid() ||
      moment(target.value, 'M/D/YYYY', true).isValid()
    if (
      !target.value ||
      target.value.length < 8 ||
      target.value.length > 10 ||
      !isValidDate
    ) {
      setDataRange([
        {
          ...dataRange[0],
          [target.name]: null,
        },
      ])
    } else if (
      target.name === 'endDate' &&
      (!dataRange[0].startDate ||
        isAfter(dataRange[0].startDate, new Date(target.value)))
    ) {
      setDataRange([
        {
          ...dataRange[0],
          startDate: new Date(target.value),
          endDate: new Date(target.value),
        },
      ])
    } else if (
      target.name === 'startDate' &&
      !target.value &&
      dataRange[0].endDate
    ) {
      setDataRange([
        {
          ...dataRange[0],
          startDate: null,
          endDate: null,
        },
      ])
    } else if (
      target.name === 'startDate' &&
      dataRange[0].startDate &&
      isBefore(dataRange[0].endDate, new Date(target.value))
    ) {
      setDataRange([
        {
          ...dataRange[0],
          startDate: new Date(target.value),
          endDate: new Date(target.value),
        },
      ])
    } else {
      setDataRange([
        {
          ...dataRange[0],
          [target.name]: new Date(target.value),
        },
      ])
    }
  }

  const handleKeyDown = (e) => {
    if (e.keyCode === 13) {
      handleInputBlur(e)
    }
  }

  const handleApply = () => {
    onApply({
      startDate: dataRange[0].startDate
        ? format(dataRange[0].startDate, 'MM/dd/yyyy')
        : null,
      endDate: dataRange[0].endDate
        ? format(dataRange[0].endDate, 'MM/dd/yyyy')
        : null,
    })
    setAnchorEl(null)
  }

  useEffect(() => {
    setDataRange([
      {
        ...defaultRange,
        startDate: value.startDate ? new Date(value.startDate) : null,
        endDate: value.endDate ? new Date(value.endDate) : null,
        // startDate: value.startDate ? startOfDay(new Date(moment.utc(value.startDate).add(moment().format('Z').charAt(0) === '-' ? 1 : 0, 'days').format('MM/DD/YYYY'))) : null,
        // endDate: value.endDate ? endOfDay(new Date(moment.utc(value.endDate).add(moment().format('Z').charAt(0) === '+' && moment().format('Z') !== '+00:00' ? -1 : 0, 'days').format('MM/DD/YYYY'))) : null,
      },
    ])
  }, [value]) // eslint-disable-line

  useEffect(() => {
    setTempRange({
      startDate: dataRange[0].startDate
        ? format(dataRange[0].startDate, 'MM/dd/yyyy')
        : '',
      endDate: dataRange[0].endDate
        ? format(dataRange[0].endDate, 'MM/dd/yyyy')
        : '',
    })
  }, [dataRange, setTempRange])

  const { startDate, endDate } = dataRange[0]

  const displayStaticRanges = useMemo(() => {
    if (customRanges) {
      return createStaticRanges(customRanges(defaultRanges, defineds))
    }
    return !removeAll ? staticRanges : staticRangesOptional
  }, [
    customRanges,
    removeAll,
    staticRanges,
    staticRangesOptional,
    defaultRanges,
    defineds,
  ])

  const renderDisplayedValue = () => {
    if (!placeholder && !startDate && !endDate) {
      return displayStaticRanges[0].label
    }

    const existedLabel = displayStaticRanges.find(
      (s) =>
        isSameDay(startDate, s.range().startDate) &&
        isSameDay(endDate, s.range().endDate),
    )

    if (existedLabel && existedLabel.label !== 'Custom') {
      return existedLabel.label
    }

    if (startDate || endDate) {
      return `${startDate ? format(startDate, 'MM/dd/yyyy') : '--/--/--'} - ${endDate ? format(endDate, 'MM/dd/yyyy') : '--/--/--'}`
    }

    return ''
  }

  const { width } = useWindowWidth()
  return (
    <Container
      className={`${classes.container}${className ? ` ${className}` : ''}`}
      data-testid={dataTestId}
    >
      {leftItem && <div className={classes.leftItem}>{leftItem}</div>}
      <TextInput
        checkRemoveCursor
        label={labelText}
        legend={legend}
        className={anchorEl ? classes.inputOpen : ''}
        InputProps={{
          inputProps: {
            value: renderDisplayedValue(),
            readOnly: true,
            placeholder: placeholder || '',
          },
          classes: {
            adornedEnd: classes.adornedEnd,
          },
          endAdornment: anchorEl ? (
            <InputAdornment position="end" className={classes.inputAdornment}>
              <ArrowDropUp color="primary" />
            </InputAdornment>
          ) : onClear && (value.startDate || value.endDate) ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                onClear()
              }}
              className={classes.buttonClear}
            >
              <Icon name="close" color={COLORS.palette.primaryBlue} size={10} />
            </IconButton>
          ) : (
            <InputAdornment position="end" className={classes.inputAdornment}>
              <KeyboardArrowDown color="primary" />
            </InputAdornment>
          ),
        }}
        onClick={handleOpen}
      />

      <Popover
        className={classes.popover}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Paper
          elevation={0}
          className={classes.paper}
          data-testid={paperDataTestId}
        >
          <DateRangePicker
            className={`${classes.customDateRangePicker} ${tableClassName} ${disablePreview ? ' disablePreview' : ''}`}
            onChange={(item) => handleChange(item)}
            showMonthAndYearPickers={false}
            moveRangeOnFirstSelection={false}
            editableDateInputs
            weekdayDisplayFormat="EEEEEE"
            monthDisplayFormat="MMMM yyyy"
            startDatePlaceholder="MM/DD/YYYY"
            endDatePlaceholder="MM/DD/YYYY"
            rangeColors={[]}
            inputRanges={[]}
            staticRanges={displayStaticRanges}
            fixedHeight={fixedHeight}
            ranges={dataRange}
            shownDate={
              dataRange?.[0] && dataRange?.[0]?.startDate
                ? dataRange[0]?.startDate
                : defineds.startOfToday
            }
            maxDate={
              disableFuture && maxDate
                ? maxDate
                : disableFuture
                  ? defineds.startOfToday
                  : undefined
            }
            renderStaticRangeLabel={(definedRange) => (
              <CustomStaticRangeLabelContent
                {...definedRange}
                staticRanges={displayStaticRanges}
                dataRange={dataRange}
                setDisabledPreview={setDisabledPreview}
                onClickRangeLabel={onClickRangeLabel}
              />
            )}
            {...(isTableMode(width)
              ? DAY_PICKER_TABLET_PROPS
              : DAY_PICKER_DESKTOP_PROPS)}
          />

          <div className={classes.footer}>
            <div className={classes.fieldGroup}>
              <div
                className={classes.fieldControl}
                data-testid="time-range-startDate"
              >
                <span className={classes.fieldLabel}>From</span>
                <TextInput
                  className={clsx('dateSelect__startDate')}
                  name="startDate"
                  placeholder="MM/DD/YYYY"
                  value={tempRange.startDate || ''}
                  onChange={(e) => handleInputChange(e)}
                  onBlur={(e) => handleInputBlur(e)}
                  onKeyDown={(e) => handleKeyDown(e)}
                />
              </div>
              <div
                className={classes.fieldControl}
                data-testid="time-range-endDate"
              >
                <span className={classes.fieldLabel}>To</span>
                <TextInput
                  className={clsx('dateSelect__endDate')}
                  name="endDate"
                  placeholder="MM/DD/YYYY"
                  value={tempRange.endDate || ''}
                  onChange={(e) => handleInputChange(e)}
                  onBlur={(e) => handleInputBlur(e)}
                  onKeyDown={(e) => handleKeyDown(e)}
                />
              </div>
            </div>
            <div className={classes.applyBtnWrapper}>
              <Button
                className={clsx('dateSelect__btn__filter', classes.applyBtn)}
                onClick={() => handleApply()}
                label="Apply Filter"
                data-testid="time-range-apply-btn"
              />
            </div>
          </div>
        </Paper>
      </Popover>
    </Container>
  )
}

TimeRange.defaultProps = {
  value: {
    startDate: null,
    endDate: null,
  },
  labelText: '',
  onApply: () => {},
  onChange: () => {},
  placeholder: '',
  className: '',
  tableClassName: '',
  onClear: null,
  leftItem: null,
  disableFuture: true,
  maxDate: undefined,
  legend: undefined,
  removeAll: false,
  fixedHeight: true,
  timeZone: '',
  disableToday: false,
  customRanges: null,
  onClickRangeLabel: () => {},
  dataTestId: 'date-picker',
  paperDataTestId: 'date-picker-paper',
  isAddOptionLast12Months: false,
}

TimeRange.propTypes = {
  classes: PropTypes.shape().isRequired,
  className: PropTypes.string,
  tableClassName: PropTypes.string,
  value: PropTypes.shape(),
  labelText: PropTypes.node,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onApply: PropTypes.func,
  onClear: PropTypes.func,
  leftItem: PropTypes.node,
  disableFuture: PropTypes.bool,
  maxDate: PropTypes.string,
  legend: PropTypes.string,
  removeAll: PropTypes.bool,
  fixedHeight: PropTypes.bool,
  timeZone: PropTypes.string,
  disableToday: PropTypes.bool,
  customRanges: PropTypes.func,
  onClickRangeLabel: PropTypes.func,
  dataTestId: PropTypes.string,
  paperDataTestId: PropTypes.string,
  isAddOptionLast12Months: PropTypes.bool,
}

export default withStyles(style)(TimeRange)
